import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "./i18n";
import { supabaseV2 } from "./supabase";
import { fetchAllPages } from "./workspace-data";
import { seasonErrorMessage, useSeasonScope } from "./seasons";
import { formatDateTime } from "./format";
import { copyToClipboard, shareWhatsAppOrCopy } from "./share";
import type { Session, SessionCallup } from "../types";

const tSess = (key: string, vars?: Record<string, unknown>): string =>
  i18n.t(`sessions:${key}`, vars ?? {}) as string;

export function useSessionCallups(teamId: string) {
  const scope = useSeasonScope();
  return useQuery({
    queryKey: ["event-callups", teamId, scope.key],
    enabled: scope.ready,
    queryFn: async () => {
      return fetchAllPages((from, to) => {
        let query = supabaseV2
          .from("session_callups")
          .select("id, session_id, player_id, status, confirmed_at, notified_at, sessions!inner(team_id, season_id)")
          .eq("sessions.team_id", teamId);
        if (scope.seasonId) query = query.eq("sessions.season_id", scope.seasonId);
        return query.range(from, to);
      }) as Promise<SessionCallup[]>;
    },
  });
}

export function useSessionCallupUpsert(teamId: string) {
  const queryClient = useQueryClient();
  const queryKey = ["event-callups", teamId];
  const mutationKey = ["callup-upsert", teamId];
  return useMutation({
    mutationKey,
    mutationFn: async ({ eventId, playerId, shouldCallup }: { eventId: string; playerId: string; shouldCallup: boolean }) => {
      const { error } = await supabaseV2.from("session_callups").upsert(
        {
          session_id: eventId,
          player_id: playerId,
          status: shouldCallup ? "confirmed" : "removed",
          confirmed_at: shouldCallup ? new Date().toISOString() : null,
        },
        { onConflict: "session_id,player_id" }
      );
      if (error) throw error;
    },
    // Il tocco si vede subito: si possono convocare piu' giocatori di fila
    // senza aspettare il salvataggio di ciascuno.
    onMutate: async ({ eventId, playerId, shouldCallup }) => {
      await queryClient.cancelQueries({ queryKey });
      const status: SessionCallup["status"] = shouldCallup ? "confirmed" : "removed";
      // Come il trigger sul DB: cambiando stato l'avviso va rifatto.
      queryClient.setQueriesData<SessionCallup[]>({ queryKey }, (old) => {
        if (!old) return old;
        const exists = old.some((c) => c.session_id === eventId && c.player_id === playerId);
        return exists
          ? old.map((c) => (c.session_id === eventId && c.player_id === playerId ? { ...c, status, notified_at: null } : c))
          : [...old, { id: `pending-${playerId}`, session_id: eventId, player_id: playerId, status, notified_at: null }];
      });
    },
    // Si rilegge solo a coda finita, anche in caso di errore: cosi' un tocco
    // non riuscito torna allo stato reale.
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useCallupPresentMutation(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, playerIds }: { eventId: string; playerIds: string[] }) => {
      if (playerIds.length === 0) return;
      const { error } = await supabaseV2.from("session_callups").upsert(
        playerIds.map((playerId) => ({
          session_id: eventId,
          player_id: playerId,
          status: "confirmed",
          confirmed_at: new Date().toISOString(),
        })),
        { onConflict: "session_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["event-callups", teamId] }),
  });
}

export function useNotifyCallupsMutation(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<{ sent: number; already: number }> => {
      const { data, error } = await supabaseV2.rpc("notify_session_callups", {
        _session_id: sessionId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return {
        sent: Number(row?.sent ?? 0),
        already: Number(row?.already ?? 0),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-callups", teamId] });
    },
  });
}

export function useSessionUpdateMutation(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      eventId,
      title,
      date,
      time,
      deadlineDate,
      deadlineTime,
      location,
      notes,
      status,
    }: {
      eventId: string;
      title: string;
      date: string;
      time: string;
      deadlineDate?: string;
      deadlineTime?: string;
      location: string;
      notes: string;
      status: string;
    }) => {
      const startsAt = new Date(`${date}T${time || "12:00"}`).toISOString();
      const dDate = deadlineDate || date;
      const dTime = deadlineTime || time || "12:00";
      const deadline = new Date(`${dDate}T${dTime}`).toISOString();
      const { error } = await supabaseV2
        .from("sessions")
        .update({
          title,
          starts_at: startsAt,
          response_deadline_at: deadline,
          location: location || null,
          notes: notes || null,
          status,
        })
        .eq("id", eventId)
        .eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "all"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "training"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "match_day"] });
      queryClient.invalidateQueries({ queryKey: ["seasons", teamId] });
    },
    onError: (e: any) => {
      window.alert(seasonErrorMessage(e) || e?.message || String(e));
    },
  });
}

export function useMagicLinkMutation(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ event, mode }: { event: Session; mode: "open" | "copy" }) => {
      const token = createPublicToken();
      await createSessionToken(event, token);
      const url = sessionPublicUrl(token);
      if (mode === "copy") {
        const ok = await copyToClipboard(url);
        if (!ok) throw new Error(tSess("message.copyError"));
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["responses", teamId] });
    },
  });
}

// Costruisce il messaggio per condividere il link presenze. Restituisce
// il testo: la copia/apertura WhatsApp e' lasciata al chiamante (dialog
// con bottoni espliciti, evita race condition clipboard async su mobile).
export function useSessionMessageMutation() {
  return useMutation({
    mutationFn: async (event: Session): Promise<{ text: string; title: string }> => {
      const token = createPublicToken();
      await createSessionToken(event, token);
      const text = [
        tSess("message.confirmAttendance", { title: event.title }),
        event.starts_at ? formatDateTime(event.starts_at) : "",
        event.location ? tSess("message.location", { location: event.location }) : "",
        tSess("message.linkLine", { url: sessionPublicUrl(token) }),
      ]
        .filter(Boolean)
        .join("\n");
      return { text, title: tSess("message.confirmAttendanceTitle", { title: event.title }) };
    },
  });
}

export function useCallupListMessageMutation() {
  return useMutation({
    mutationFn: async ({ event, names }: { event: Session; names: string[] }): Promise<{ text: string; title: string }> => {
      if (names.length === 0) throw new Error(tSess("message.noCallups"));
      const lines = [
        tSess("message.callupsHeader", { title: event.title }),
        event.starts_at ? formatDateTime(event.starts_at) : "",
        event.location ? tSess("message.location", { location: event.location }) : "",
        "",
        tSess("message.callupsCount", { count: names.length }),
        ...names.map((n) => `• ${n}`),
      ].filter((line, idx, arr) => line !== "" || (idx > 0 && arr[idx - 1] !== ""));
      return { text: lines.join("\n"), title: tSess("message.callupsTitle", { title: event.title }) };
    },
  });
}

export async function createSessionToken(event: Session, token: string) {
  const tokenHash = await sha256Bytea(token);
  const { error } = await supabaseV2.from("session_response_tokens").insert({
    session_id: event.id,
    player_id: null,
    token_hash: tokenHash,
    label: event.title,
    expires_at: null,
  });
  if (error) throw error;
}

export function sessionPublicUrl(token: string) {
  return `${window.location.origin}${window.location.pathname}?t=${encodeURIComponent(token)}`;
}

export function createPublicToken() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function sha256Bytea(value: string) {
  const bytes = new TextEncoder().encode(value);
  const hash = await window.crypto.subtle.digest("SHA-256", bytes);
  return `\\x${[...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export function shiftIsoDays(value: string | null | undefined, days: number) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return date.toISOString();
}
